import React, { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import DialogContent from '@mui/material/DialogContent';

import Dialog from '@/components/mui/Dialog';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';
import JECellListOptionForm, {
  type JECellSearchHandler,
  type JEIndexFormValues,
} from '@/components/Tables/Cells/ListCell/withManageListItemModal/form';
import { useJEIndexForm } from '@/domain/journal/queries';
import type { JEIndexFormField } from '@/domain/journal/schemas';

interface DialogManageJECellListOptionProps {
  companyId: string | null;
  documentID: string;
  cellName: string;
  // '' asks for the create form; a set value asks for that row's edit form.
  cellValue: string;
  // What the user has typed into the cell but not committed.
  name: string;
  isEdit: boolean;
  cancel: () => void;
  onSubmit: (values: JEIndexFormValues) => void;
  onSearchHandler: JECellSearchHandler;
}

const DialogManageJECellListOption = ({
  companyId,
  documentID,
  cellName,
  cellValue,
  name,
  cancel,
  onSubmit,
  isEdit,
  onSearchHandler,
}: DialogManageJECellListOptionProps) => {
  const { data, isLoading } = useJEIndexForm({ companyId, documentID, cellName, cellValue });

  // Gated on isLoading, never on data: a failed read leaves data undefined for
  // good, and the dialog then shows what it showed before — a form with no
  // fields — rather than hanging on the spinner.
  const fields: JEIndexFormField[] = data ?? [];

  // this is necessary to switch between fields using the TAB button
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Dialog
      onKeyDown={onKeyDown}
      open
      onClose={cancel}
      maxWidth="sm"
      withCloseBtn
      draggable
      withContent={false}
      withActions={false}
      title={
        isEdit ? (
          <FormattedMessage id="modal.document.edit.supplier.title" defaultMessage="Edit" />
        ) : (
          <FormattedMessage id="modal.document.create.supplier.title" defaultMessage="Create" />
        )
      }
    >
      {isLoading ? (
        <DialogContent sx={{ position: 'relative', minHeight: 300 }}>
          <CircularProgressWithBackdrop
            BackdropProps={{
              sx: {
                position: 'absolute',
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          />
        </DialogContent>
      ) : (
        <DialogContent sx={{ pb: 0 }}>
          <JECellListOptionForm
            fields={fields}
            name={name}
            cancel={cancel}
            onSubmit={onSubmit}
            onSearchHandler={onSearchHandler}
          />
        </DialogContent>
      )}
    </Dialog>
  );
};

export default memo(DialogManageJECellListOption);
